/**
 * The app: the query cache, the style sheet, the browser router and the page-view tracker around AppRoutes, which holds
 * the routes and the sign-in and data providers inside them (AppRoutes.tsx has the route notes: the public pages, the
 * layout, the providers each page gets). Split out so a test can draw a real route (src/test-support/renderRoute.mjs).
 *
 * Used by: main.tsx only. The full route table, with each screen's file, is in
 * private/docs-from-bloom-next/APP-ARCHITECTURE.md; keep the two in step when a route changes.
 *
 * Env vars: none read here.
 *
 * Surprising: the future flag v7_relativeSplatPath is on, so React Router 6 already resolves relative links under a
 * splat route ("*") the way version 7 will.
 *
 * @module App
 */

import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/config/queryClient';
import { GlobalStyle } from '@/shared/styles/base';
import { AnalyticsRouteTracker } from '@/shared/components/AnalyticsRouteTracker';
import { AppRoutes } from './AppRoutes';

/**
 * The app: the query cache, the router and the routes with their sign-in and data providers.
 *
 * @returns The whole application.
 */
export default function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <GlobalStyle />
      <Router future={{ v7_relativeSplatPath: true }}>
        <AnalyticsRouteTracker />
        <AppRoutes />
      </Router>
    </QueryClientProvider>
  );
}
